import { AlertTriangle, CalendarClock, Check, ChevronDown, Clock3, FileVideo2, RefreshCcw, ShieldCheck, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { providerLabels } from '../data';
import type { ApprovalTarget, Provider, TikTokCreatorInfo, TikTokPrivacyOption } from '../types';
import { PlatformBadge } from './PlatformBadge';

const providers: Provider[] = ['tiktok', 'instagram', 'facebook', 'youtube'];

export const demoTikTokCreatorInfo: TikTokCreatorInfo = {
  integrationId: 'DEMO',
  provider: 'tiktok',
  creator: {
    username: 'northstarleague',
    nickname: 'Northstar League',
  },
  privacyOptions: ['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'FOLLOWER_OF_CREATOR', 'SELF_ONLY'],
  interactions: { commentsDisabled: false, duetDisabled: false, stitchDisabled: true },
  maxVideoDurationSeconds: 600,
  checkedAt: 'demo',
};

interface PublishModalProps {
  onClose: () => void;
  onScheduled: (summary: string) => void;
  onApprove?: (request: PublishRequest) => Promise<void>;
  connectedProviders?: Provider[];
  demo?: boolean;
  tenantId?: string;
  tiktokIntegrationId?: string;
  initialCaption?: string;
  connectedDestinations?: Partial<Record<Provider, { name?: string; accountId?: string }>>;
}

export interface PublishRequest {
  caption: string;
  date: string;
  time: string;
  targets: ApprovalTarget[];
  file?: File;
  fileDurationSeconds?: number;
}

export function PublishModal({
  onClose,
  onScheduled,
  onApprove,
  connectedProviders,
  demo = false,
  tenantId,
  tiktokIntegrationId,
  initialCaption,
  connectedDestinations,
}: PublishModalProps) {
  const [caption, setCaption] = useState(() => initialCaption ?? (demo ? 'Three angles. One impossible comeback. Watch the moment the bracket flipped. #NorthstarLeague #FinalRound' : ''));
  const [date, setDate] = useState(() => tomorrow());
  const [time, setTime] = useState('18:30');
  const [confirmedRights, setConfirmedRights] = useState(false);
  const [file, setFile] = useState<File>();
  const [fileDurationSeconds, setFileDurationSeconds] = useState<number>();
  const [durationError, setDurationError] = useState<string>();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [creatorInfo, setCreatorInfo] = useState<TikTokCreatorInfo>();
  const [creatorInfoError, setCreatorInfoError] = useState<string>();
  const [creatorInfoLoading, setCreatorInfoLoading] = useState(false);
  const [creatorInfoAttempt, setCreatorInfoAttempt] = useState(0);
  const fileReadVersion = useRef(0);
  const [targets, setTargets] = useState<ApprovalTarget[]>([
    { provider: 'tiktok', selected: connectedProviders ? connectedProviders.includes('tiktok') : true },
    { provider: 'instagram', selected: connectedProviders ? connectedProviders.includes('instagram') : true, privacy: 'Professional account' },
    { provider: 'facebook', selected: false, privacy: 'Page feed' },
    { provider: 'youtube', selected: connectedProviders ? connectedProviders.includes('youtube') : true },
  ]);

  const selected = targets.filter((target) => target.selected);
  const tiktokTarget = targets.find((target) => target.provider === 'tiktok')!;
  const tiktokSelected = tiktokTarget.selected;
  const activeCreatorInfo = demo && tiktokSelected ? demoTikTokCreatorInfo : creatorInfo;

  useEffect(() => {
    if (!file || typeof URL.createObjectURL !== 'function') {
      setPreviewUrl(undefined);
      return;
    }
    const nextUrl = URL.createObjectURL(file);
    setPreviewUrl(nextUrl);
    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);

  useEffect(() => {
    if (!tiktokSelected) {
      setCreatorInfo(undefined);
      setCreatorInfoError(undefined);
      setCreatorInfoLoading(false);
      return;
    }
    if (demo) {
      initializeTikTokInteractions(setTargets, demoTikTokCreatorInfo);
      return;
    }
    if (!tenantId || !tiktokIntegrationId) {
      setCreatorInfo(undefined);
      setCreatorInfoError('Connect an active TikTok creator before reviewing this destination.');
      return;
    }
    const controller = new AbortController();
    let active = true;
    setCreatorInfo(undefined);
    setCreatorInfoError(undefined);
    setCreatorInfoLoading(true);
    void api<TikTokCreatorInfo>(`/tenants/${tenantId}/integrations/${tiktokIntegrationId}/tiktok-creator-info`, {
      tenantId,
      signal: controller.signal,
    }).then((info) => {
      if (!active) return;
      setCreatorInfo(info);
      initializeTikTokInteractions(setTargets, info);
    }).catch((reason: unknown) => {
      if (!active || (reason instanceof DOMException && reason.name === 'AbortError')) return;
      setCreatorInfoError(reason instanceof Error ? reason.message : 'TikTok creator settings could not be loaded.');
    }).finally(() => {
      if (active) setCreatorInfoLoading(false);
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [creatorInfoAttempt, demo, tenantId, tiktokIntegrationId, tiktokSelected]);

  const tiktokIncomplete = tiktokSelected && !isTikTokTargetComplete(tiktokTarget, activeCreatorInfo);
  const youtubeIncomplete = selected.some((target) => target.provider === 'youtube' && (
    !target.privacy || !target.youtubeTitle?.trim() || target.madeForKids === undefined
  ));
  const missingConnection = connectedProviders ? selected.some((target) => !connectedProviders.includes(target.provider)) : false;
  const mediaRequired = !demo && selected.some((target) => ['tiktok', 'instagram', 'youtube'].includes(target.provider)) && !file;
  const tiktokMediaUnsupported = Boolean(tiktokSelected && file && !file.type.startsWith('video/'));
  const tiktokDurationPending = Boolean(tiktokSelected && file?.type.startsWith('video/') && fileDurationSeconds === undefined && !durationError);
  const tiktokDurationTooLong = Boolean(
    tiktokSelected && activeCreatorInfo && fileDurationSeconds !== undefined &&
    fileDurationSeconds > activeCreatorInfo.maxVideoDurationSeconds,
  );
  const tiktokCaptionTooLong = tiktokSelected && caption.length > 2_200;
  const facebookVideoUnsupported = isFacebookVideoUnsupported(demo, selected, file?.type);
  const creatorUnavailable = tiktokSelected && !demo && (creatorInfoLoading || Boolean(creatorInfoError) || !creatorInfo);
  const canSchedule = selected.length > 0 && !tiktokIncomplete && !youtubeIncomplete && !missingConnection &&
    !mediaRequired && !tiktokMediaUnsupported && !tiktokDurationPending && !tiktokDurationTooLong && !tiktokCaptionTooLong && !facebookVideoUnsupported &&
    !creatorUnavailable && !durationError && confirmedRights && Boolean(date && time && caption.trim()) && !busy;

  const summary = useMemo(() => selected.map((target) => providerLabels[target.provider]).join(', '), [selected]);

  function update(provider: Provider, change: Partial<ApprovalTarget>) {
    setTargets((existing) => existing.map((target) => target.provider === provider ? { ...target, ...change } : target));
  }

  async function chooseFile(nextFile?: File) {
    const version = ++fileReadVersion.current;
    setFile(nextFile);
    setFileDurationSeconds(undefined);
    setDurationError(undefined);
    if (!nextFile || !nextFile.type.startsWith('video/')) return;
    try {
      const duration = await readVideoDuration(nextFile);
      if (version === fileReadVersion.current) setFileDurationSeconds(duration);
    } catch {
      if (version === fileReadVersion.current) setDurationError('ThisBot could not verify the selected video duration. Choose a supported video file.');
    }
  }

  async function approve() {
    if (!canSchedule) return;
    setBusy(true);
    setError(undefined);
    try {
      if (onApprove) await onApprove({ caption, date, time, targets: selected, file, fileDurationSeconds });
      onScheduled(`${demo ? 'Demo scheduled' : 'Scheduled'} for ${date} at ${time} on ${summary}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The post could not be scheduled.');
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="publish-modal" role="dialog" aria-modal="true" aria-labelledby="publish-title">
        <header className="publish-modal__head">
          <div>
            <span className="section-index">IMMUTABLE APPROVAL SNAPSHOT</span>
            <h2 id="publish-title">Review every destination</h2>
            {demo && <span className="demo-review-badge">DEMO DATA · NOTHING WILL PUBLISH</span>}
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close"><X size={19} /></button>
        </header>

        <div className="publish-modal__grid">
          <div className="publish-preview">
            <div className="publish-preview__media">
              {previewUrl && file?.type.startsWith('video/')
                ? <video src={previewUrl} controls playsInline aria-label="Exact video preview" />
                : previewUrl && file?.type.startsWith('image/')
                  ? <img src={previewUrl} alt="Exact post preview" />
                  : <FileVideo2 size={36} />}
              <strong>{file?.name ?? (demo ? 'final-round-comeback.mp4' : 'Choose the exact media file')}</strong>
              <span>{file
                ? `${(file.size / 1_048_576).toFixed(1)} MB · ${file.type}${fileDurationSeconds ? ` · ${formatDuration(fileDurationSeconds)}` : ''}`
                : demo ? '1080 × 1920 · 0:34 · sample source v3' : 'Select the exact recorded media to preview and freeze.'}</span>
              <label className="button button--light" htmlFor="publish-media">{file ? 'Replace media' : 'Choose media'}</label>
              <input id="publish-media" className="visually-hidden" type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm" onChange={(event) => void chooseFile(event.target.files?.[0])} />
            </div>
            <div className="media-hash"><ShieldCheck size={14} /><span>Media frozen for this approval</span><code>{file ? 'SHA-256 is calculated before upload' : demo ? 'sha256: demo-9e4a…81c2' : 'Calculated after you choose media'}</code></div>
          </div>

          <div className="publish-form">
            <label className="field-label">Post copy<textarea value={caption} onChange={(event) => setCaption(event.target.value)} rows={5} /><span>{caption.length} characters · editable before approval</span></label>
            <div className="schedule-fields">
              <label className="field-label">Date<input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
              <label className="field-label">Local time<input type="time" value={time} onChange={(event) => setTime(event.target.value)} /></label>
              <div className="timezone-field"><Clock3 size={15} /><span>America/Phoenix</span></div>
            </div>

            <fieldset className="target-fieldset">
              <legend>Destinations</legend>
              {providers.map((provider) => {
                const target = targets.find((item) => item.provider === provider)!;
                return (
                  <div className={`target-row ${target.selected ? 'is-selected' : ''}`} key={provider}>
                    <label className="target-row__main">
                      <input type="checkbox" checked={target.selected} onChange={(event) => update(provider, { selected: event.target.checked })} />
                      <PlatformBadge provider={provider} />
                      <span><strong>{providerLabels[provider]}</strong><small>{destination(provider, activeCreatorInfo, connectedDestinations, demo)}</small></span>
                    </label>
                    {target.selected && provider === 'tiktok' && (
                      <TikTokReviewControls
                        target={target}
                        creatorInfo={activeCreatorInfo}
                        loading={creatorInfoLoading}
                        error={creatorInfoError}
                        demo={demo}
                        onUpdate={(change) => update('tiktok', change)}
                        onRetry={() => setCreatorInfoAttempt((attempt) => attempt + 1)}
                      />
                    )}
                    {target.selected && provider === 'youtube' && (
                      <div className="target-options target-options--tiktok">
                        <label>Video title <input value={target.youtubeTitle ?? ''} maxLength={100} onChange={(event) => update(provider, { youtubeTitle: event.target.value })} placeholder="Add a YouTube title" /></label>
                        <div className="toggle-grid">
                          <label>Visibility <select value={target.privacy ?? ''} onChange={(event) => update(provider, { privacy: event.target.value || undefined })}><option value="">Choose…</option><option value="public">Public</option><option value="unlisted">Unlisted</option><option value="private">Private</option></select></label>
                          <label>Audience <select value={target.madeForKids === undefined ? '' : String(target.madeForKids)} onChange={(event) => update(provider, { madeForKids: event.target.value === '' ? undefined : event.target.value === 'true' })}><option value="">Choose…</option><option value="false">Not made for kids</option><option value="true">Made for kids</option></select></label>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </fieldset>

            <label className="rights-confirm"><input type="checkbox" checked={confirmedRights} onChange={(event) => setConfirmedRights(event.target.checked)} /><span><strong>I reviewed this post and have the rights to publish it.</strong><small>The approval record includes my user ID, each destination, content hash, settings, and scheduled time.</small></span></label>
            {tiktokIncomplete && !creatorUnavailable && <p className="inline-warning"><AlertTriangle size={13} /> Complete every required TikTok privacy, AI, disclosure, and consent choice.</p>}
            {tiktokCaptionTooLong && <p className="inline-warning"><AlertTriangle size={13} /> TikTok captions can contain at most 2,200 characters.</p>}
            {tiktokMediaUnsupported && <p className="inline-warning"><AlertTriangle size={13} /> ThisBot’s TikTok Direct Post path currently requires a video.</p>}
            {facebookVideoUnsupported && <p className="inline-warning"><AlertTriangle size={13} /> Facebook video delivery is not enabled yet because provider processing cannot be reconciled safely. Deselect Facebook or choose a supported image/text post.</p>}
            {tiktokDurationTooLong && <p className="inline-warning"><AlertTriangle size={13} /> This video is longer than the connected creator’s current TikTok limit.</p>}
            {durationError && <p className="inline-warning"><AlertTriangle size={13} /> {durationError}</p>}
            {missingConnection && <p className="inline-warning"><AlertTriangle size={13} /> Connect every selected destination before scheduling.</p>}
            {mediaRequired && <p className="inline-warning"><AlertTriangle size={13} /> The selected platforms require the exact media file to be frozen into this approval.</p>}
            {error && <p className="inline-warning"><AlertTriangle size={13} /> {error}</p>}
          </div>
        </div>

        <footer className="publish-modal__foot">
          <div><CalendarClock size={16} /><span><strong>{selected.length} destinations</strong><small>{summary || 'Select at least one destination'}</small></span></div>
          <div><button className="button button--outline" onClick={onClose}>Save as draft</button><button className="button button--blue" disabled={!canSchedule} onClick={() => void approve()}><Check size={16} /> {busy ? 'Freezing approval…' : 'Approve & schedule'}</button></div>
        </footer>
      </section>
    </div>
  );
}

export function TikTokReviewControls({
  target,
  creatorInfo,
  loading,
  error,
  demo,
  onUpdate,
  onRetry,
}: {
  target: ApprovalTarget;
  creatorInfo?: TikTokCreatorInfo;
  loading: boolean;
  error?: string;
  demo: boolean;
  onUpdate: (change: Partial<ApprovalTarget>) => void;
  onRetry: () => void;
}) {
  const branded = target.disclosure === 'branded' || target.disclosure === 'both';
  const ownBrand = target.disclosure === 'brand' || target.disclosure === 'both';
  const paidPartnershipBlocked = target.privacy === 'SELF_ONLY';

  return (
    <div className="target-options target-options--tiktok" data-testid="tiktok-review-controls">
      {loading && <div className="creator-info-state"><span className="loading-mark" /><span><strong>Checking TikTok creator settings…</strong><small>Privacy and interactions come directly from TikTok.</small></span></div>}
      {error && <div className="creator-info-error"><AlertTriangle size={15} /><span><strong>Creator check required</strong><small>{error}</small></span><button type="button" onClick={onRetry}><RefreshCcw size={13} /> Retry</button></div>}
      {creatorInfo && (
        <>
          <div className="creator-identity">
            {creatorInfo.creator.avatarUrl
              ? <img src={creatorInfo.creator.avatarUrl} alt="" referrerPolicy="no-referrer" />
              : <span className="creator-avatar">{creatorInfo.creator.nickname.slice(0, 1).toUpperCase()}</span>}
            <span><strong>{creatorInfo.creator.nickname}</strong><small>@{creatorInfo.creator.username} · maximum video {formatDuration(creatorInfo.maxVideoDurationSeconds)}</small></span>
            <em>{demo ? 'SAMPLE' : 'LIVE CHECK'}</em>
          </div>

          <label>Privacy <span className="required">required · no default</span>
            <span className="select-wrap"><select aria-label="TikTok privacy" value={target.privacy ?? ''} onChange={(event) => onUpdate({ privacy: event.target.value || undefined })}>
              <option value="">Choose privacy…</option>
              {creatorInfo.privacyOptions.map((privacy) => <option key={privacy} value={privacy} disabled={privacy === 'SELF_ONLY' && branded}>{privacyLabel(privacy)}</option>)}
            </select><ChevronDown size={14} /></span>
          </label>

          <div className="interaction-grid" aria-label="TikTok interaction settings">
            <label className={creatorInfo.interactions.commentsDisabled ? 'is-disabled' : ''} title={creatorInfo.interactions.commentsDisabled ? 'Disabled in the creator’s TikTok settings' : undefined}>
              <input type="checkbox" checked={target.comments === true} disabled={creatorInfo.interactions.commentsDisabled} onChange={(event) => onUpdate({ comments: event.target.checked })} />
              <span><strong>Allow comments</strong><small>{creatorInfo.interactions.commentsDisabled ? 'Unavailable for this creator' : 'Off until you enable it'}</small></span>
            </label>
            <label className={creatorInfo.interactions.duetDisabled ? 'is-disabled' : ''} title={creatorInfo.interactions.duetDisabled ? 'Disabled in the creator’s TikTok settings' : undefined}>
              <input type="checkbox" checked={target.duet === true} disabled={creatorInfo.interactions.duetDisabled} onChange={(event) => onUpdate({ duet: event.target.checked })} />
              <span><strong>Allow Duet</strong><small>{creatorInfo.interactions.duetDisabled ? 'Unavailable for this creator' : 'Off until you enable it'}</small></span>
            </label>
            <label className={creatorInfo.interactions.stitchDisabled ? 'is-disabled' : ''} title={creatorInfo.interactions.stitchDisabled ? 'Disabled in the creator’s TikTok settings' : undefined}>
              <input type="checkbox" checked={target.stitch === true} disabled={creatorInfo.interactions.stitchDisabled} onChange={(event) => onUpdate({ stitch: event.target.checked })} />
              <span><strong>Allow Stitch</strong><small>{creatorInfo.interactions.stitchDisabled ? 'Unavailable for this creator' : 'Off until you enable it'}</small></span>
            </label>
          </div>

          <div className="tiktok-explicit-grid">
            <label>AI-generated or significantly AI-edited? <span className="required">required</span>
              <select aria-label="TikTok AI-generated disclosure" value={target.aigc === undefined ? '' : String(target.aigc)} onChange={(event) => onUpdate({ aigc: event.target.value === '' ? undefined : event.target.value === 'true' })}>
                <option value="">Choose…</option><option value="false">No</option><option value="true">Yes</option>
              </select>
            </label>
            <label>Promotes a brand, product, or service? <span className="required">required</span>
              <select aria-label="TikTok commercial content disclosure" value={target.commercialContent === undefined ? '' : String(target.commercialContent)} onChange={(event) => {
                if (event.target.value === '') onUpdate({ commercialContent: undefined, disclosure: undefined });
                else if (event.target.value === 'false') onUpdate({ commercialContent: false, disclosure: 'none' });
                else onUpdate({ commercialContent: true, disclosure: undefined });
              }}>
                <option value="">Choose…</option><option value="false">No</option><option value="true">Yes</option>
              </select>
            </label>
          </div>

          {target.commercialContent === true && (
            <div className="commercial-choices">
              <strong>Who is this content promoting?</strong>
              <label><input type="checkbox" checked={ownBrand} onChange={(event) => onUpdate({ disclosure: toggleDisclosure(target.disclosure, 'brand', event.target.checked) })} /> Your brand <small>Label: Promotional content</small></label>
              <label className={paidPartnershipBlocked ? 'is-disabled' : ''} title={paidPartnershipBlocked ? 'Branded content visibility cannot be set to private.' : undefined}><input type="checkbox" checked={branded} disabled={paidPartnershipBlocked} onChange={(event) => onUpdate({ disclosure: toggleDisclosure(target.disclosure, 'branded', event.target.checked) })} /> Branded content <small>Label: Paid partnership</small></label>
              {!ownBrand && !branded && <p>You need to indicate if your content promotes yourself, a third party, or both.</p>}
            </div>
          )}

          <label className="tiktok-consent">
            <input type="checkbox" checked={target.uploadConsent === true} onChange={(event) => onUpdate({ uploadConsent: event.target.checked })} />
            <span><strong>I expressly consent to upload this exact media to TikTok.</strong><small>{branded
              ? <>By posting, you agree to TikTok’s <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noreferrer">Branded Content Policy</a> and <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer">Music Usage Confirmation</a>.</>
              : <>By posting, you agree to TikTok’s <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noreferrer">Music Usage Confirmation</a>.</>}</small></span>
          </label>
          <p className="tiktok-processing-note">TikTok may take a few minutes to process and display the post. ThisBot will keep the publication in processing until TikTok reports its final status.</p>
        </>
      )}
    </div>
  );
}

export function isTikTokTargetComplete(target: ApprovalTarget, info?: TikTokCreatorInfo): boolean {
  if (!info || !target.privacy || !info.privacyOptions.includes(target.privacy as TikTokPrivacyOption)) return false;
  if (typeof target.comments !== 'boolean' || typeof target.duet !== 'boolean' || typeof target.stitch !== 'boolean') return false;
  if (info.interactions.commentsDisabled && target.comments) return false;
  if (info.interactions.duetDisabled && target.duet) return false;
  if (info.interactions.stitchDisabled && target.stitch) return false;
  if (typeof target.aigc !== 'boolean' || typeof target.commercialContent !== 'boolean' || target.uploadConsent !== true) return false;
  if (target.commercialContent === false && target.disclosure !== 'none') return false;
  if (target.commercialContent === true && !['brand', 'branded', 'both'].includes(target.disclosure ?? '')) return false;
  if ((target.disclosure === 'branded' || target.disclosure === 'both') && target.privacy === 'SELF_ONLY') return false;
  return true;
}

export function isFacebookVideoUnsupported(demo: boolean, targets: ApprovalTarget[], fileType?: string): boolean {
  return !demo && Boolean(fileType?.startsWith('video/')) && targets.some((target) => target.selected && target.provider === 'facebook');
}

export function toggleDisclosure(
  current: ApprovalTarget['disclosure'],
  kind: 'brand' | 'branded',
  enabled: boolean,
): ApprovalTarget['disclosure'] {
  const brand = current === 'brand' || current === 'both';
  const branded = current === 'branded' || current === 'both';
  const nextBrand = kind === 'brand' ? enabled : brand;
  const nextBranded = kind === 'branded' ? enabled : branded;
  if (nextBrand && nextBranded) return 'both';
  if (nextBrand) return 'brand';
  if (nextBranded) return 'branded';
  return undefined;
}

function initializeTikTokInteractions(
  setTargets: React.Dispatch<React.SetStateAction<ApprovalTarget[]>>,
  info: TikTokCreatorInfo,
) {
  setTargets((existing) => existing.map((target) => target.provider !== 'tiktok' ? target : {
    ...target,
    privacy: undefined,
    comments: false,
    duet: false,
    stitch: false,
    ...(info.interactions.commentsDisabled ? { comments: false } : {}),
    ...(info.interactions.duetDisabled ? { duet: false } : {}),
    ...(info.interactions.stitchDisabled ? { stitch: false } : {}),
  }));
}

function privacyLabel(value: TikTokPrivacyOption): string {
  return ({
    PUBLIC_TO_EVERYONE: 'Everyone',
    MUTUAL_FOLLOW_FRIENDS: 'Friends (mutual followers)',
    FOLLOWER_OF_CREATOR: 'Followers',
    SELF_ONLY: 'Only me',
  })[value];
}

function tomorrow(): string {
  const date = new Date(Date.now() + 86_400_000);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function destination(
  provider: Provider,
  creatorInfo: TikTokCreatorInfo | undefined,
  connectedDestinations: Partial<Record<Provider, { name?: string; accountId?: string }>> | undefined,
  demo: boolean,
): string {
  if (provider === 'tiktok' && creatorInfo) return `@${creatorInfo.creator.username}`;
  if (demo) return ({
    tiktok: 'Creator check required',
    instagram: '@northstar.league · Professional',
    facebook: 'Northstar League Page',
    youtube: '@NorthstarLeague',
  })[provider];
  const destination = connectedDestinations?.[provider];
  if (destination?.name) return destination.name;
  if (destination?.accountId) return `Destination ••••${destination.accountId.slice(-5)}`;
  return 'Connection required';
}

function formatDuration(seconds: number): string {
  const rounded = Math.round(seconds);
  const minutes = Math.floor(rounded / 60);
  return `${minutes}:${String(rounded % 60).padStart(2, '0')}`;
}

function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const cleanup = () => URL.revokeObjectURL(url);
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      const duration = video.duration;
      cleanup();
      if (!Number.isFinite(duration) || duration <= 0) reject(new Error('invalid duration'));
      else resolve(duration);
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('metadata unavailable'));
    };
    video.src = url;
  });
}
